import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { JourneyButton } from '@/components/ui/journey-button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Enhanced mobile error logging
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const deviceInfo = {
      isMobile,
      userAgent: navigator.userAgent,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      url: window.location.href,
      hostname: window.location.hostname
    };

    console.error('ErrorBoundary caught an error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      deviceInfo
    });

    // Update state with error details
    this.setState({
      error,
      errorInfo
    });

    // Call optional error callback
    this.props.onError?.(error, errorInfo);

    // Log error to analytics/monitoring service
    this.logErrorToService(error, errorInfo);
  }

  private logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    // In production, send to error monitoring service (Sentry, LogRocket, etc.)
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry.captureException(error, { extra: errorInfo });
      console.error('Error logged to monitoring service:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString()
      });
    }
  };

  private handleRetry = () => {
    // Clear error state to retry rendering
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  private handleGoHome = () => {
    // Navigate to home and clear error state
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl border-red-200 shadow-xl">
            <CardHeader className="text-center pb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <CardTitle className="text-2xl text-red-800 mb-2">
                Something went wrong
              </CardTitle>
              <p className="text-red-600">
                We encountered an unexpected error in your Red Helicopter journey.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Error Details (Development only) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="bg-gray-50 rounded-lg p-4 text-sm">
                  <h3 className="font-semibold text-gray-800 mb-2">Error Details:</h3>
                  <p className="text-red-600 mb-2">{this.state.error.message}</p>
                  {this.state.error.stack && (
                    <pre className="text-xs text-gray-600 overflow-auto max-h-32">
                      {this.state.error.stack}
                    </pre>
                  )}
                </div>
              )}

              {/* User-friendly message */}
              <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-400">
                <h3 className="font-semibold text-blue-800 mb-2">What can you do?</h3>
                <ul className="text-blue-700 space-y-1 text-sm">
                  <li>• Try refreshing the page or clicking "Try Again"</li>
                  <li>• Check your internet connection</li>
                  <li>• If the problem persists, please contact support</li>
                  <li>• Your progress has been automatically saved</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <JourneyButton
                  onClick={this.handleRetry}
                  variant="primary"
                  size="lg"
                  className="flex-1"
                  icon={<RefreshCw className="w-4 h-4" />}
                  iconPosition="left"
                >
                  Try Again
                </JourneyButton>
                <JourneyButton
                  onClick={this.handleGoHome}
                  variant="secondary"
                  size="lg"
                  className="flex-1"
                  icon={<Home className="w-4 h-4" />}
                  iconPosition="left"
                >
                  Go Home
                </JourneyButton>
              </div>

              {/* Support Information */}
              <div className="text-center pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-2">
                  Need help? Contact our support team:
                </p>
                <a
                  href="https://www.redhelicopter.com/community"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  support@redheli.com
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for easier usage
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) => {
  return (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );
};

export default ErrorBoundary;